import { Box, Typography, IconButton, CircularProgress } from '@mui/material'
import DragHandle from '@mui/icons-material/DragHandle'
import CheckIcon from '@mui/icons-material/Check'
import { useFormikContext } from 'formik'
import i18n from 'lib/i18'
import { CSS } from '@dnd-kit/utilities'
import { contentLanguages } from 'lib/languages'
import { MasterProduct } from 'types/api/product'
import { ProductImage } from 'views/Products/ProductImage'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import theme from 'lib/theme'
import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { ProductCardWrapper } from './Product.styled'

type FormikValuesType = {
  equivalentProducts: { gtin: string; referencedTradeItemTypeCode: string }[]
  alternativeProducts: { gtin: string; referencedTradeItemTypeCode: string }[]
}

type ProductCardTypes = {
  product: MasterProduct
  handleAddProduct?: (obj: {
    gtin: string
    referencedTradeItemTypeCode: string
  }) => void
  referencedTradeItemTypeCode?: string
  handleDeleteProduct?: (gtin: string) => void
}

export const ProductCard = ({
  product,
  handleAddProduct,
  referencedTradeItemTypeCode,
  handleDeleteProduct,
}: ProductCardTypes) => {
  const [hover, setHover] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const { values } = useFormikContext()
  const imgInfo =
    product.tradeItem.tradeItemInformation.extensions.dgMediaModule?.media || []
  const nameOptions =
    product.tradeItem.tradeItemInformation.extensions.tradeItemDescriptionModule
      ?.tradeItemDescriptionInformation?.descriptionShort
  const translatedName = nameOptions
    ? nameOptions.find((name) => name['@languageCode'] === i18n.language)?.$ ||
      nameOptions.find((name) => name['@languageCode'] === contentLanguages[0])
        ?.$
    : ''
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: product.gtin,
    })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  const handleAdd = () => {
    if (handleAddProduct && referencedTradeItemTypeCode) {
      setIsAdding(true)
      handleAddProduct({
        gtin: product.gtin,
        referencedTradeItemTypeCode,
      })
      setIsAdding(false)
    }
  }

  const displayButton = () => {
    if (handleAddProduct && referencedTradeItemTypeCode) {
      if (isAdding) {
        return (
          <Box display='flex' justifyContent='center'>
            <CircularProgress />
          </Box>
        )
      }
      const productList =
        referencedTradeItemTypeCode === 'EQUIVALENT'
          ? (values as FormikValuesType).equivalentProducts
          : (values as FormikValuesType).alternativeProducts
      if (productList.find((val) => val.gtin === product.gtin)) {
        return <CheckIcon />
      }
      return (
        <IconButton onClick={handleAdd}>
          <AddIcon />
        </IconButton>
      )
    }
    if (handleDeleteProduct && hover) {
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            marginRight: theme.spacing(1),
          }}>
          <IconButton
            sx={{ marginRight: theme.spacing(1) }}
            onClick={() => handleDeleteProduct(product.gtin)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
    return null
  }

  return (
    <ProductCardWrapper
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      handleDeleteProduct={Boolean(handleDeleteProduct)}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      <Box display='flex' alignItems='center'>
        {handleDeleteProduct && (
          <DragHandle
            sx={{
              fontSize: '18px',
              visibility: hover ? 'visible' : 'hidden',
              margin: '8px',
            }}
          />
        )}
        <ProductImage
          imgInfo={imgInfo?.length > 0 ? imgInfo[0].mediaStorageKey : ''}
        />
        <Box ml={1}>
          <Typography>{translatedName || ''}</Typography>
          <Typography>{product.gtin}</Typography>
        </Box>
      </Box>
      {displayButton()}
    </ProductCardWrapper>
  )
}
